import React, { useState } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { buildApiUrl } from "../config";  // ✅ Import config

const defaultBlocks = [
  { id: "starter", label: "Starter Activity" },
  { id: "main", label: "Main Activity" },
  { id: "plenary", label: "Plenary/Reflection" },
  { id: "extension", label: "Extension" },
];

export default function DesignTab() {
  // Worksheet Generator State
  const [wsSubject, setWsSubject] = useState("");
  const [wsTopic, setWsTopic] = useState("");
  const [wsYear, setWsYear] = useState("");
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);

  // Section 2: Drag-and-drop lesson blocks
  const [lessonBlocks, setLessonBlocks] = useState(defaultBlocks);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(lessonBlocks);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setLessonBlocks(items);
  };

  const handleGenerateWorksheet = async () => {
    setLoading(true);
    const res = await axios.post(buildApiUrl("/api/generate-worksheet"), {
      subject: wsSubject,
      topic: wsTopic,
      year_group: wsYear,
    });
    setWorksheet(res.data.worksheet);
    setLoading(false);
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold text-gray-800">Lesson Designer</h2>

      {/* Worksheet Generator */}
      <div className="bg-white border rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-3">AI Worksheet Generator</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input
            className="border p-2 rounded"
            placeholder="Subject (e.g., English)"
            value={wsSubject}
            onChange={(e) => setWsSubject(e.target.value)}
          />
          <input
            className="border p-2 rounded"
            placeholder="Topic (e.g., Fractions)"
            value={wsTopic}
            onChange={(e) => setWsTopic(e.target.value)}
          />
          <input
            className="border p-2 rounded"
            placeholder="Year Group (e.g., 4)"
            value={wsYear}
            onChange={(e) => setWsYear(e.target.value)}
          />
        </div>
        <button
          onClick={handleGenerateWorksheet}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {loading ? "Generating..." : "Generate Worksheet"}
        </button>
      </div>

      {/* Worksheet Preview and Download (Section 2) */}
      <div className="bg-white border rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-3">Worksheet Preview</h3>
        {worksheet ? (
          <div>
            <div className="mb-4">
              <h4 className="font-bold">{worksheet.title}</h4>
              <p className="text-sm text-gray-600 mb-2">
                Subject: {worksheet.subject} | Year Group: {worksheet.year_group}
              </p>
              {worksheet.instructions && (
                <p className="mb-2">
                  <strong>Instructions:</strong> {worksheet.instructions}
                </p>
              )}
              {worksheet.reading_passage && (
                <div className="mb-3">
                  <strong>Reading Passage:</strong>
                  <div className="bg-gray-100 rounded p-2 mt-1">
                    {worksheet.reading_passage}
                  </div>
                </div>
              )}
              <div className="border-t border-gray-300 pt-2">
                {worksheet.questions &&
                  worksheet.questions.map((q, i) => (
                    <div key={i} className="mb-4">
                      <p className="font-medium">
                        {i + 1}. {q.question}
                      </p>
                      {q.type === "multiple_choice" && (
                        <ul className="ml-5 list-disc">
                          {q.options.map((opt, j) => (
                            <li key={j}>{opt}</li>
                          ))}
                        </ul>
                      )}
                      {q.type === "short_answer" && (
                        <p>
                          <em>Answer: {q.answer}</em>
                        </p>
                      )}
                      {q.type === "true_false" && (
                        <p>
                          Answer:{" "}
                          <strong>{q.answer ? "True" : "False"}</strong>
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">No worksheet generated yet.</p>
        )}
      </div>

      {/* Section 2: Drag-and-Drop Lesson Flow Designer */}
      <div className="bg-white border rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-3">Lesson Flow Designer</h3>
        <p className="text-gray-500 mb-4">
          Drag and drop lesson blocks to design your lesson structure:
        </p>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="blocks">
            {(provided) => (
              <ul
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-3"
              >
                {lessonBlocks.map((block, index) => (
                  <Draggable
                    key={block.id}
                    draggableId={block.id}
                    index={index}
                  >
                    {(provided) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded shadow flex items-center"
                      >
                        <span className="font-medium text-blue-800">
                          {block.label}
                        </span>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}
